import { DEFAULT_FLOW_CONFIG, flowDefaultAction, flowEventRules } from './flow.js';
/** Pure content/display block types — they never produce a feedback evaluation. Everything else
 * (answerable inputs + sensors) is gradable. Mirrors teacher-interface's `isGradableBlockType`. */
const DISPLAY_BLOCK_TYPES = new Set(['text', 'image', 'sound', 'dataviz']);
/** A block that can produce a FeedbackState (is "gradable"). Decided by TYPE — an answerable input or
 * sensor (anything that isn't a display type) — so a freshly-added block counts BEFORE its
 * `feedback.states` are seeded (they only appear on its first edit), matching when Flow's events apply.
 * Falls back to the data shape for a typeless block (legacy/test data that carries states directly). */
function isGradable(b) {
    if (b?.type)
        return !DISPLAY_BLOCK_TYPES.has(b.type);
    const states = b?.data?.feedback?.states;
    return Array.isArray(states);
}
const targetOf = (a) => (a.type === 'goto' ? a.targetTaskId : null);
const wiredTo = (a) => a.type === 'goto' && a.targetTaskId !== null;
function event(id, kind, category, action, title) {
    return {
        id, kind, category, action,
        targetTaskId: targetOf(action),
        wired: wiredTo(action),
        endsHere: action.type === 'end',
        ...(title !== undefined ? { title } : {}),
    };
}
/** An action a teacher deliberately authored — a live goto or an End — as opposed to the neutral
 * fall-through (`stay`/`next`). Used so an already-wired branch survives even on a blockless task. */
const isAuthored = (a) => (a.type === 'goto' && a.targetTaskId !== null) || a.type === 'end';
export function deriveTaskEvents(task) {
    const cfg = task.flow ?? DEFAULT_FLOW_CONFIG;
    const correct = event('correct', 'correct', 'route', cfg.allCorrectAction);
    const branches = [];
    const gradable = (task.blocks ?? []).some(isGradable);
    // A gradable task exposes ALL its branch events (open, to wire). A non-gradable task has nothing to
    // answer, so it exposes NO open branches — but a branch that's ALREADY wired (a goto/End authored via
    // the task-level flow menu, or left behind when the last answerable block was removed) still surfaces,
    // so the board wire it draws stays visible AND disconnectable in the port instead of becoming orphaned.
    const include = (a) => gradable || isAuthored(a);
    const fallback = flowDefaultAction(cfg);
    if (include(fallback))
        branches.push(event('incorrect', 'incorrect', 'fallback', fallback));
    if (include(cfg.noAnswerAction))
        branches.push(event('noAnswer', 'noAnswer', 'gate', cfg.noAnswerAction));
    for (const rule of flowEventRules(cfg)) {
        if (include(rule.action))
            branches.push(event(rule.id, 'special', 'gate', rule.action, rule.title));
    }
    return { correct, branches };
}
//# sourceMappingURL=task-events.js.map